import { useTranslation } from 'react-i18next';
import GlassCard from '../ui/GlassCard';

interface StatItem {
  key: string;
  value: number;
  labelKey: string;
  icon: React.ReactNode;
}

interface StatsCardProps {
  stats?: StatItem[];
  isPlaceholder?: boolean;
}

const PLACEHOLDER_STATS: Omit<StatItem, 'icon'>[] = [
  { key: 'views', value: 0, labelKey: 'views' },
  { key: 'scans', value: 0, labelKey: 'scans' },
  { key: 'vcards', value: 0, labelKey: 'vcards' },
];

export default function StatsCard({ stats, isPlaceholder = true }: StatsCardProps) {
  const { t } = useTranslation();

  const displayStats = stats || PLACEHOLDER_STATS.map(stat => ({
    ...stat,
    icon: getIconForStat(stat.key),
  }));

  return (
    <GlassCard className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('dashboard.stats.title')}
        </h2>
        {isPlaceholder && (
          <span className="text-xs px-2 py-1 bg-mk-pink-100 text-mk-pink-600 rounded-full">
            {t('dashboard.stats.comingSoon')}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {displayStats.map((stat) => (
          <div
            key={stat.key}
            className={`text-center p-4 rounded-xl ${
              isPlaceholder ? 'bg-gray-50' : 'bg-gradient-to-br from-mk-pink-50 to-white'
            }`}
          >
            <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
              isPlaceholder ? 'bg-gray-200 text-gray-400' : 'bg-mk-pink-100 text-mk-pink-600'
            }`}>
              {stat.icon}
            </div>
            <p className={`text-2xl font-bold ${isPlaceholder ? 'text-gray-300' : 'text-gray-900'}`}>
              {isPlaceholder ? '—' : stat.value}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t(`dashboard.stats.${stat.labelKey}`)}
            </p>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

function getIconForStat(key: string) {
  switch (key) {
    case 'views':
      return <EyeIcon className="w-5 h-5" />;
    case 'scans':
      return <QrCodeIcon className="w-5 h-5" />;
    case 'vcards':
      return <DownloadIcon className="w-5 h-5" />;
    default:
      return <ChartIcon className="w-5 h-5" />;
  }
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

function QrCodeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}
